import { useState } from "react";
import { publicProfileCountry } from "../utilities/publicProfileLocation";
import { ProfileInfo } from "../../features/profile/presentation/interfaces/EditProfileInterface";
import { Row } from "./Row";
import { Column } from "./Column";
import { TextComponent } from "./TextComponent";
import { Map } from "../icons/Map";
import { Label } from "./Label";
import { formatDate } from "../constants/constants";
import { User } from "../icons/User";
import { Partner } from "../../features/settings/domain/entities/Partner";
import { NavigationButton } from "../../features/match/presentation/components/NavigationButton";
import { RemoteImage } from "./RemoteImage";

interface UserViewProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
    pictures?: ProfileInfo[] | string[];
    name?: string;
    location?: string;
    verify?: JSX.Element
    resume?: string;
    aboutMe?: string;
    desires?: string[];
    interest?: string[];
    report?: JSX.Element | ((payload: { currentImageUrl: string; currentImageIndex: number }) => JSX.Element);
    scroll?: boolean;
    full?: boolean;
    className?: string;
    isBlur?: boolean;
    last_logged_in_time?: string;
    show_last_logged?: boolean;
    partner?: Partner;
    country: string;
    actionOverlay?: React.ReactNode;
    detailsClassName?: string;
    fixedPhotoOnMobile?: boolean;
}

export const UserView = ({ children, pictures, name, resume, location, verify, aboutMe, desires, interest, report, scroll, full = false, className, isBlur = true,show_last_logged, last_logged_in_time, partner, country, actionOverlay, detailsClassName, fixedPhotoOnMobile = false, ...props }: UserViewProps) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const pictureCount = pictures?.length ?? 0;

    const handleNextImage = () => {
        if (pictureCount > 1) {
            setCurrentImageIndex((currentImageIndex + 1) % pictureCount);
        }
    };

    const handlePrevImage = () => {
        if (pictureCount > 1) {
            setCurrentImageIndex((currentImageIndex - 1 + pictureCount) % pictureCount);
        }
    };

    const getSrc = (): string => {
        if (pictures) {
            const currentPicture = pictures[currentImageIndex];
            if (typeof currentPicture === "string") {
                return currentPicture;
            } else if (typeof currentPicture === "object") {
                return currentPicture.url;
            }
        }
        return "";
    };

    const renderedReport =
        typeof report === "function"
            ? report({ currentImageUrl: getSrc(), currentImageIndex })
            : report;
    const displayPlace = publicProfileCountry(country, location);
    const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
    const joinedDesires = desires
        ?.filter(Boolean)
        .map((value) => capitalize(value))
        .join(", ");

    const details = (
        <Column className={`gap-3 sm:gap-4 px-3 pb-3 ${detailsClassName || ""}`}>
            <Row mainAxisAlignment="between" crossAxisAlignment="start">
                <Column className="gap-1  overflow-hidden whitespace-normal break-words">
                    <Row crossAxisAlignment="center" className="gap-2">
                        <TextComponent variant="h3" className="max-sm:text-2xl font-semibold">
                            {name}
                        </TextComponent>
                        {verify}
                    </Row>
                    {show_last_logged && last_logged_in_time && (
                        <TextComponent variant="nh6" className="font-semibold text-graySecondary">
                            Last login was {formatDate(last_logged_in_time, true)}
                        </TextComponent>
                    )}
                    <TextComponent variant="nh6" className="text-grayLight2 font-[500]">
                        {resume}
                    </TextComponent>
                    {displayPlace && !fixedPhotoOnMobile && <Row crossAxisAlignment="center">
                        <Map />
                        <TextComponent variant="nh6">
                            {displayPlace}
                        </TextComponent>
                    </Row>}
                </Column>
                {renderedReport !== undefined && !fixedPhotoOnMobile &&
                    <div className="right-0" >
                        {renderedReport}
                    </div>
                }
            </Row>
            <div className="w-full border-t-[0.5px] border-grayLight" />
            {partner && partner.status === "accepted" &&
                <Column className="gap-1">
                    <TextComponent variant="nh4" className="font-semibold">
                        Partner
                    </TextComponent>
                    <Row className="text-white gap-2">
                        {partner.profile_pictures && partner.profile_pictures?.url ? (
                            <RemoteImage
                                className="w-10 aspect-square object-cover rounded-full"
                                src={partner.profile_pictures.url}
                                draggable={false}
                                alt={`profile-${partner.username}`}
                            />
                        ) : (
                            <User className="bg-gray-200 p-2 text-white w-10 aspect-square object-cover rounded-full"/>
                        )}
                        <Column mainAxisAlignment="center">
                            <TextComponent
                                variant="nh6"
                                className="font-semibold text-grayLight2"
                            >
                                {partner.username}
                            </TextComponent>
                        </Column>
                    </Row>
                </Column>
            }
            {aboutMe &&
                <Column>
                    <TextComponent variant="nh4" className="font-semibold">
                        About me
                    </TextComponent>
                    <TextComponent variant="nh6" className="text-grayLight2 whitespace-pre-wrap">
                        {aboutMe}
                    </TextComponent>
                </Column>
            }
            {displayPlace && fixedPhotoOnMobile &&
                <Column>
                    <TextComponent variant="nh4" className="font-semibold">
                        Living in
                    </TextComponent>
                    <TextComponent variant="nh6" className="text-grayLight2">
                        {displayPlace}
                    </TextComponent>
                </Column>
            }
            {desires &&
                <Column className="gap-1">
                    <TextComponent variant="nh4" className="font-semibold">
                        Desires
                    </TextComponent>
                    {fixedPhotoOnMobile ? (
                        <TextComponent variant="nh6" className="text-grayLight2 font-[500]">
                            {joinedDesires}
                        </TextComponent>
                    ) : (
                        <Row className="gap-1 sm:gap-1.5 flex-wrap">
                            {desires.map((interest, index) => (
                                <Label
                                    key={index}
                                    title={interest}
                                    variant="circle"
                                    color="white"
                                />
                            ))}
                        </Row>
                    )}
                </Column>
            }
            {interest && interest[0] !== null &&
                <Column className="gap-1">
                    <TextComponent variant="nh4" className="font-semibold">
                        Interest
                    </TextComponent>
                    <Row className="gap-1 sm:gap-1.5 flex-wrap">
                        {interest.map((interest, index) => (
                            <Label
                                key={index}
                                title={interest}
                                variant="circle"
                                className={fixedPhotoOnMobile ? "!border-pink/10 !bg-pink/10 !text-black px-2.5 py-1 text-sm font-semibold" : ""}
                            />
                        ))}
                    </Row>
                </Column>
            }
        </Column>
    );

    return (
        <Column
            className={`w-full sm:w-[30rem] h-full ${!full && "sm:max-h-[43rem]"} bg-white ${fixedPhotoOnMobile ? "max-md:flex max-md:flex-col max-md:overflow-hidden md:overflow-y-auto" : "md:overflow-y-auto"} overflow-x-hidden gap-4 sm:gap-5 sm:rounded-xl ${scroll && "scrollbar-hide"} ${className}`}
            {...props}
        >
            <picture className={`bg-black w-full h-[25rem] md:h-[30rem] md:rounded-xl md:rounded-b-none select-none relative ${fixedPhotoOnMobile ? "max-md:!h-[40svh] max-md:min-h-[18rem] max-md:max-h-[44rem] max-md:flex-none max-md:rounded-none" : ""}`}>
                <RemoteImage
                    className={`w-full h-[25rem] md:h-[30rem] ${fixedPhotoOnMobile ? "max-md:!h-[40svh] max-md:min-h-[18rem] max-md:max-h-[44rem]" : ""}
                        ${pictures && isBlur && (pictures[currentImageIndex] as ProfileInfo)?.is_profile !== true &&
                        ((pictures[currentImageIndex] as ProfileInfo)?.state === 0
                        || (pictures[currentImageIndex] as ProfileInfo)?.is_public === false) ? "blur-md" : ""}`}
                    src={getSrc()}
                    alt={`image-${currentImageIndex}`}
                    draggable={false}
                />
                {pictureCount > 1 && fixedPhotoOnMobile && (
                    <>
                        <button
                            type="button"
                            className="absolute top-0 left-0 h-full w-1/2 cursor-pointer"
                            onClick={handlePrevImage}
                            aria-label="Prev Image"
                        >
                            <span className="absolute left-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/20 text-2xl leading-none text-white/85">
                                ‹
                            </span>
                        </button>

                        <button
                            type="button"
                            className="absolute top-0 right-0 h-full w-1/2 cursor-pointer"
                            onClick={handleNextImage}
                            aria-label="Next Image"
                        >
                            <span className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/20 text-2xl leading-none text-white/85">
                                ›
                            </span>
                        </button>
                    </>
                )}
                {pictureCount > 1 && !fixedPhotoOnMobile && (
                    <>
                        <div className="absolute top-0 left-0 w-1/2 h-full" onClick={handlePrevImage}>
                            <NavigationButton
                                className="left-0 text-white"
                                ariaLabel="Prev Image"
                                isReversed={false}
                                isBackground={false}
                            />
                        </div>

                        <div className="absolute top-0 right-0 w-1/2 h-full" onClick={handleNextImage}>
                            <NavigationButton
                                className="right-0 text-white"
                                ariaLabel="Next Image"
                                isBackground={false}
                                isReversed={true}
                            />
                        </div>
                    </>
                )}
                {fixedPhotoOnMobile && renderedReport !== undefined && (
                    <div className="absolute right-4 top-3 z-20">
                        {renderedReport}
                    </div>
                )}
                <Row
                    mainAxisAlignment="center"
                    className={fixedPhotoOnMobile ? "absolute left-7 top-4 gap-1.5" : "absolute left-1/2 bottom-4 gap-1.5 transform -translate-x-1/2"}
                    flexible={fixedPhotoOnMobile}
                >
                    {pictures?.map((_, index) => (
                        fixedPhotoOnMobile ? (
                            <button
                                key={index}
                                type="button"
                                aria-label={`Show photo ${index + 1}`}
                                onClick={() => setCurrentImageIndex(index)}
                                className={`h-[3px] w-8 rounded-full border border-white/60 ${index === currentImageIndex ? "bg-white" : "bg-grayLight/70"}`}
                            />
                        ) : (
                            <svg
                                key={index}
                                width={index === currentImageIndex ? "6" : "7"}
                                height="6"
                                viewBox="0 0 7 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={() => setCurrentImageIndex(index)}
                                className="cursor-pointer"
                            >
                                <circle
                                    cx={index === currentImageIndex ? "3" : "3.5"}
                                    cy="3"
                                    r={index === currentImageIndex ? "3" : "2.5"}
                                    fill={index === currentImageIndex ? "white" : "none"}
                                    stroke={index === currentImageIndex ? "none" : "white"}
                                    strokeOpacity={index === currentImageIndex ? "0" : "0.46"}
                                />
                            </svg>
                        )
                    ))}
                </Row>
                {actionOverlay && (
                    <div className="absolute left-0 right-0 bottom-0 z-10 translate-y-1/2 pointer-events-none">
                        <div className="pointer-events-auto">
                            {actionOverlay}
                        </div>
                    </div>
                )}
            </picture>
            {fixedPhotoOnMobile ? (
                <div className="max-md:flex-1 max-md:min-h-0 max-md:overflow-y-auto max-md:overscroll-contain max-md:scrollbar-hide max-md:pb-[calc(5.75rem+env(safe-area-inset-bottom))] md:contents">
                    {details}
                    {children}
                </div>
            ) : (
                <>
                    {details}
                    {children}
                </>
            )}
        </Column>
    )
}
